import { useState, useMemo, useEffect } from "react";
import { Typography, Button } from "antd";
import { useNotifications } from "../../hooks/useNotifications";
import DataState from "../../components/DataState/DataState";

const { Title } = Typography;

// ─── Icon Paths ───────────────────────────────────────────────────────────────

const ICONS = {
  warning: 'M10 2C5.58 2 2 5.58 2 10s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6zm.5-10H9v5h1.5zm0 6H9v1.5h1.5z',
  check: 'M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z',
  info: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z',
  approval: 'M12 1C5.925 1 1 5.925 1 12s4.925 11 11 11 11-4.925 11-11S18.075 1 12 1zm-2 16l-5-5 1.4-1.4L10 14.2l7.6-7.6L19 8l-9 9z',
  bell: 'M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z',
  x: 'M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z',
};



const NOTIF_CATEGORY_STYLE = {
  Alert:    { icon: ICONS.warning,     bg: '#fde7e9', color: '#a4262c', border: '#f5b0b3' },
  Action:   { icon: ICONS.check,       bg: '#fce8d3', color: '#ca5010', border: '#f5c6a0' },
  Update:   { icon: ICONS.info,        bg: '#e8f4fd', color: '#0078d4', border: '#b3d4f5' },
  Approval: { icon: ICONS.approval,    bg: '#f3f0fd', color: '#5c2d91', border: '#c8b8f0' },
  System:   { icon: ICONS.bell,        bg: '#f0f2f5', color: '#5c6070', border: '#d6dae3' },
};

// ─── Time helper ──────────────────────────────────────────────────────────────

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function NotificationsPage() {
  const { notifications, markRead, markAllRead, dismiss, refresh } = useNotifications();
  console.log("notifications", notifications);
  const [filter, setFilter] = useState('All');

  // Each load gets an id. The page is ready only once the load with the current id has settled,
  // so the status is 'loading' in the same render that starts a request (mount and retry).
  const [loadId, setLoadId] = useState(0);
  const [settledId, setSettledId] = useState(null);
  const [loadError, setLoadError] = useState("");

  // Ensure the list is fresh when the page mounts (AppLayout owns the polling loop).
  useEffect(() => {
    let isCancelled = false;
    void Promise.resolve(refresh()).then((result) => {
      if (isCancelled) return;
      setLoadError(result?.error ? result.error.message || "Failed to load notifications" : "");
      setSettledId(loadId);
    });
    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadId]);

  const loadStatus = settledId !== loadId ? "loading" : loadError ? "error" : "ready";

  const unreadCount = useMemo(() => notifications?.filter(n => !n.read)?.length, [notifications]);
  const categories = ['All', 'Alert', 'Action', 'Approval', 'Update', 'System'];
  const filtered = useMemo(
    () => filter === 'All' ? notifications : notifications?.filter(n => n.category === filter),
    [filter, notifications],
  );

  return (
    <div style={{ maxWidth: 900, margin: "0 auto" }}>
      <Title level={4} style={{ margin: 0, marginBottom: 4 }}>
        Notifications
      </Title>

      <DataState
        status={loadStatus}
        errorTitle="Couldn't load notifications"
        error={loadError}
        onRetry={() => setLoadId((id) => id + 1)}
      >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <span style={{ color: "#8b92a5", fontSize: 13 }}>
          {notifications?.length} total · {unreadCount} unread
        </span>
        {unreadCount > 0 && (
          <Button type="primary" onClick={markAllRead}>
            Mark all read
          </Button>
        )}
      </div>

      {/* Category filter pills */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        {categories.map(cat => {
          const isActive = filter === cat;
          const count = cat === 'All' ? notifications?.length : notifications?.filter(n => n.category === cat).length;
          const style_ = cat !== 'All' ? NOTIF_CATEGORY_STYLE[cat] : null;
          return (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              style={{
                padding: '6px 14px',
                borderRadius: 20,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                border: '1px solid',
                whiteSpace: 'nowrap',
                background: isActive ? (style_?.bg ?? '#161b2e') : '#fff',
                color: isActive ? (style_?.color ?? '#fff') : '#8b92a5',
                borderColor: isActive ? (style_?.border ?? '#161b2e') : '#e5e8ee',
              }}
            >
              {cat} {count > 0 && <span>({count})</span>}
            </button>
          );
        })}
      </div>

      {/* Notifications list */}
      {filtered?.length === 0 ? (
        <div style={{ padding: 60, textAlign: 'center', color: '#8b92a5' }}>
          <p style={{ fontSize: 14, margin: 0 }}>No notifications in this category.</p>
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e5e8ee', overflow: 'hidden' }}>
          {filtered?.map((notif, i) => {
            const cs = NOTIF_CATEGORY_STYLE[notif.category];
            return (
              <div
                key={notif.id}
                onClick={() => markRead(notif.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    markRead(notif.id);
                  }
                }}
                role="button"
                tabIndex={0}
                style={{
                  display: 'flex',
                  gap: 14,
                  padding: '16px 20px',
                  borderBottom: i < filtered?.length - 1 ? '1px solid #f0f2f5' : 'none',
                  background: notif.read ? '#fff' : '#f7f9ff',
                  cursor: 'pointer',
                  transition: 'background 0.15s',
                  position: 'relative',
                }}
                onMouseEnter={e => {
                  (e.currentTarget).style.background = notif.read ? '#fafbfc' : '#f0f4fa';
                }}
                onMouseLeave={e => {
                  (e.currentTarget).style.background = notif.read ? '#fff' : '#f7f9ff';
                }}
              >
                {/* Unread dot */}
                {!notif.read && (
                  <div
                    style={{
                      position: 'absolute',
                      left: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      background: '#0078d4',
                    }}
                  />
                )}

                {/* Icon */}
                <div
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 8,
                    background: cs.bg,
                    border: `1px solid ${cs.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={cs.color} strokeWidth="1.8">
                    <path d={cs.icon} />
                  </svg>
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#161b2e' }}>{notif.title}</span>
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: 4,
                        fontSize: 11,
                        fontWeight: 600,
                        background: cs.bg,
                        color: cs.color,
                        border: `0.5px solid ${cs.border}`,
                        flexShrink: 0,
                      }}
                    >
                      {notif.category}
                    </span>
                  </div>
                  <p
                    style={{
                      fontSize: 13,
                      color: '#5c6070',
                      margin: 0,
                      lineHeight: 1.6,
                      marginBottom: 8,
                    }}
                  >
                    {notif.body}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {notif.projectId && (
                      <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#0078d4', background: '#e8f4fd', padding: '2px 8px', borderRadius: 3 }}>
                        {notif.projectId}
                      </span>
                    )}
                    <span style={{ fontSize: 11, color: '#b0b8cc', fontFamily: 'monospace' }}>{timeAgo(notif.time)}</span>
                  </div>
                </div>

                {/* Dismiss button */}
                <button
                  onClick={e => {
                    e.stopPropagation();
                    dismiss(notif.id);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#c8ccd6',
                    padding: 6,
                    borderRadius: 4,
                    flexShrink: 0,
                    display: 'flex',
                    alignSelf: 'flex-start',
                    marginTop: 0,
                  }}
                  onMouseEnter={e => ((e.currentTarget).style.color = '#a4262c')}
                  onMouseLeave={e => ((e.currentTarget).style.color = '#c8ccd6')}
                  title="Dismiss"
                >
                  <svg width={16} height={16} viewBox="0 0 24 24" fill="currentColor">
                    <path d={ICONS.x} />
                  </svg>
                </button>
              </div>
            );
          })}
        </div>
      )}
      </DataState>
    </div>
  );
}